import {
	FieldMappingSourceType,
	ZohoEntityType,
	ZohoField,
	ZohoFieldMappingPreviewResponse,
	ZohoFieldMappingPreviewRow,
	ZohoSourceField,
	ZohoTestSyncResponse,
} from "../../schema/zoho";
import {
	MappingPreviewLabels,
	MappingPreviewOutcome,
	MappingTestSyncOutcome,
} from "./MappingPreviewModal";

type Ts = (key: string, options?: Record<string, unknown>) => string;

/** Zoho numeric field types — a non-numeric value for these is rejected at write time. */
const NUMBER_FIELD_TYPES = new Set([
	"integer",
	"double",
	"currency",
	"decimal",
	"bigint",
	"long",
	"percent",
]);

/**
 * Required fields the sync satisfies outside the mapping system (e.g. the
 * record owner is assigned via user mapping or defaulted by Zoho itself), so
 * they must not be reported as missing.
 */
const REQUIRED_EXEMPT_FIELDS = new Set(["Owner"]);

/** i18n key of the tab/entity label shown as the "which mapping am I previewing" badge. */
const ENTITY_BADGE_KEY: Record<ZohoEntityType, string> = {
	[ZohoEntityType.LEAD]: "leads",
	[ZohoEntityType.CONTACT]: "contacts",
	[ZohoEntityType.ACCOUNT]: "accounts",
	[ZohoEntityType.DEAL]: "deals",
};

/** Labels for the preview modal, read from the provider's fieldMapping i18n block. */
export const buildMappingPreviewLabels = (
	ts: Ts,
	entityType: ZohoEntityType
): MappingPreviewLabels => ({
	entityBadge: ts(ENTITY_BADGE_KEY[entityType]),
	createsLabel: ts("preview.createsLabel"),
	createsDescription: ts(`preview.createsByEntity.${entityType}`),
	cancel: ts("preview.cancel"),
	title: ts("preview.title"),
	subtitle: ts("preview.subtitle"),
	prospectLabel: ts("preview.prospectLabel"),
	prospectPlaceholder: ts("preview.prospectPlaceholder"),
	run: ts("preview.run"),
	source: ts("ourField"),
	target: ts("zohoField"),
	value: ts("preview.value"),
	emptyValue: ts("preview.emptyValue"),
	aiNotRun: ts("preview.aiNotRun"),
	inactive: ts("preview.inactive"),
	fallbacksTitle: ts("preview.fallbacksTitle"),
	fallbacksHint: ts("preview.fallbacksHint"),
	noMappings: ts("preview.noMappings"),
	error: ts("preview.error"),
	missingRequiredTitle: ts("preview.warnings.missingRequiredTitle"),
	missingRequiredHint: ts("preview.warnings.missingRequiredHint"),
	syncButton: ts("preview.sync.button"),
	syncWarning: ts("preview.sync.warning"),
	syncSuccessTitle: ts("preview.sync.successTitle"),
	syncFailedTitle: ts("preview.sync.failedTitle"),
	syncOpenRecord: ts("preview.sync.openRecord"),
});

/**
 * Cross-check one resolved preview value against the target field's Zoho
 * metadata: picklist membership, numeric/boolean type fit, writability.
 */
const buildRowWarnings = (
	row: ZohoFieldMappingPreviewRow,
	field: ZohoField | undefined,
	ts: Ts
): string[] => {
	if (!field) {
		return [];
	}

	const warnings: string[] = [];

	if (field.isReadOnly || field.isCalculated) {
		warnings.push(ts("preview.warnings.readOnlyTarget"));
	}

	if (!row.resolved || row.value === null) {
		return warnings;
	}

	const hasOptions = Boolean(field.options?.length);

	// Zoho options carry both an actual_value (`value`) and a display_value
	// (`label`); a constant mapping usually holds the label the user sees in the
	// CRM. Accept a match against either, normalized, so a valid picklist value
	// whose label differs from its actual value (or differs only in case/
	// whitespace) is not wrongly flagged as rejected.
	const norm = (value: string) => value.trim().toLowerCase();
	const isAllowedOption = field.options?.some(
		(o) => norm(o.value) === norm(row.value!) || norm(o.label) === norm(row.value!)
	);

	if (hasOptions && !field.isMulti && !isAllowedOption) {
		// TEMP DIAGNOSTIC — remove once the false picklist warning is understood.

		console.warn("[mapping-preview] picklist mismatch", {
			field: field.name,
			type: field.type,
			isMulti: field.isMulti,
			value: JSON.stringify(row.value),
			options: field.options?.map((o) => ({
				label: JSON.stringify(o.label),
				value: JSON.stringify(o.value),
			})),
		});
		warnings.push(ts("preview.warnings.notAllowedValue"));
	}

	if (NUMBER_FIELD_TYPES.has(field.type) && Number.isNaN(Number(row.value))) {
		warnings.push(ts("preview.warnings.numberExpected"));
	}

	if (field.type === "boolean" && !["true", "false"].includes(row.value.toLowerCase())) {
		warnings.push(ts("preview.warnings.booleanExpected"));
	}

	return warnings;
};

/**
 * Required Zoho fields that nothing supplies: no active resolved mapping, no
 * AI mapping (runs at sync time), no automatic fallback. A sync would fail
 * with MANDATORY_NOT_FOUND for these.
 */
const buildMissingRequired = (
	response: ZohoFieldMappingPreviewResponse,
	zohoFields: ZohoField[]
): string[] => {
	const supplied = new Set([
		...response.rows
			.filter((row) => row.isActive && (row.resolved || row.isAiMapping))
			.map((row) => row.zohoField),
		...response.fallbacks.map((fallback) => fallback.zohoField),
	]);

	return zohoFields
		.filter(
			(field) =>
				field.isRequired &&
				!field.isReadOnly &&
				!field.isCalculated &&
				!REQUIRED_EXEMPT_FIELDS.has(field.name) &&
				!supplied.has(field.name)
		)
		.map((field) => field.label);
};

/**
 * Map the Zoho preview response to display rows: source paths get their
 * catalog label, constants render as 'Constant: "..."', targets resolve to the
 * Zoho field label, and each resolved value is validated against the target
 * field's metadata.
 */
export const buildZohoPreviewOutcome = (
	response: ZohoFieldMappingPreviewResponse,
	sourceFields: ZohoSourceField[],
	zohoFields: ZohoField[],
	ts: Ts
): MappingPreviewOutcome => {
	const targetLabel = (zohoField: string, storedLabel?: string) =>
		zohoFields.find((field) => field.name === zohoField)?.label ?? storedLabel ?? zohoField;

	const sourceLabel = (sourceType: FieldMappingSourceType, sourceField: string) => {
		if (sourceType === FieldMappingSourceType.CONSTANT) {
			return ts("constantLabel", { value: sourceField });
		}

		if (sourceType === FieldMappingSourceType.AI) {
			return sourceField;
		}

		return sourceFields.find((field) => field.name === sourceField)?.label ?? sourceField;
	};

	return {
		rows: response.rows.map((row) => ({
			id: row.mappingId,
			sourceLabel: sourceLabel(row.sourceType, row.sourceField),
			targetLabel: targetLabel(row.zohoField, row.zohoFieldLabel),
			value: row.value,
			resolved: row.resolved,
			isAiMapping: row.isAiMapping,
			isActive: row.isActive,
			warnings: buildRowWarnings(
				row,
				zohoFields.find((field) => field.name === row.zohoField),
				ts
			),
		})),
		fallbacks: response.fallbacks.map((fallback) => ({
			targetLabel: targetLabel(fallback.zohoField),
			value: fallback.value,
		})),
		missingRequired: buildMissingRequired(response, zohoFields),
	};
};

/** Map the Zoho test-sync response to display records for the preview modal. */
export const buildZohoTestSyncOutcome = (
	response: ZohoTestSyncResponse,
	ts: Ts
): MappingTestSyncOutcome => {
	// On a DEAL test the deep link points at the deal, not the person record.
	const personUrl = response.zohoDealId ? null : (response.recordUrl ?? null);
	const records: MappingTestSyncOutcome["records"] = [];

	if (response.zohoLeadId) {
		records.push({
			label: ts("preview.sync.leadRecord", { id: response.zohoLeadId }),
			url: response.zohoContactId ? null : personUrl,
		});
	}

	if (response.zohoContactId) {
		records.push({
			label: ts("preview.sync.contactRecord", { id: response.zohoContactId }),
			url: personUrl,
		});
	}

	if (response.zohoAccountId) {
		records.push({
			label: ts("preview.sync.accountRecord", { id: response.zohoAccountId }),
			url: null,
		});
	}

	if (response.zohoDealId) {
		records.push({
			label: ts("preview.sync.dealRecord", { id: response.zohoDealId }),
			url: response.recordUrl ?? null,
		});
	}

	return { success: response.success, error: response.error ?? null, records };
};
